import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface MainItem {
  id: number;
  ch_nfe: string;
  n_item: number;
  seq: number;
  tag_patrimonial: string;
  status_inventario: string;
  status_cadastro: string;
  descricao_normalizada: string;
  categoria_item: string;
  numero_serie: string | null;
  centro_custo: string | null;
  localizacao: string | null;
  responsavel: string | null;
  data_aquisicao: string | null;
  vida_util_meses: number | null;
  observacoes: string | null;
  valor_total: number;
  created_at: string;
  // Dados enriquecidos da NF-e
  n_nf?: string;
  serie?: string;
  emit_xnome?: string;
  dh_emi?: string;
}

interface FinancialSubitem {
  id: number;
  main_item_id: number;
  sub_code: string;
  sub_type: string;
  valor: number;
  status_inventario: string;
}

export default function AssetsRegister() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { chNfe } = useParams<{ chNfe?: string }>();

  const [mainItems, setMainItems] = useState<MainItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MainItem | null>(null);
  const [subitems, setSubitems] = useState<FinancialSubitem[]>([]);
  const [loadingSubitems, setLoadingSubitems] = useState(false);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Estados para edição do centro de custo
  const [editingCentroCusto, setEditingCentroCusto] = useState<number | null>(null);
  const [centroCustoValue, setCentroCustoValue] = useState<string>('');
  const [savingCentroCusto, setSavingCentroCusto] = useState(false);
  const [showCentroCustoDropdown, setShowCentroCustoDropdown] = useState(false);

  // Estados para edição da data de aquisição
  const [editingDataAquisicao, setEditingDataAquisicao] = useState<number | null>(null);
  const [dataAquisicaoValue, setDataAquisicaoValue] = useState<string>('');
  const [savingDataAquisicao, setSavingDataAquisicao] = useState(false);

  // Estados para edição do responsável
  const [editingResponsavel, setEditingResponsavel] = useState<number | null>(null);
  const [responsavelValue, setResponsavelValue] = useState<string>('');
  const [savingResponsavel, setSavingResponsavel] = useState(false);
  const [showResponsavelDropdown, setShowResponsavelDropdown] = useState(false);

  // Estados para edição da localização
  const [editingLocalizacao, setEditingLocalizacao] = useState<number | null>(null);
  const [localizacaoValue, setLocalizacaoValue] = useState<string>('');
  const [savingLocalizacao, setSavingLocalizacao] = useState(false);
  const [showLocalizacaoDropdown, setShowLocalizacaoDropdown] = useState(false);

  // Estados para envio ao coletor
  const [sendingToCollector, setSendingToCollector] = useState(false);
  const [selectedItemsForCollector, setSelectedItemsForCollector] = useState<Set<number>>(new Set());

  // Lista de centros de custo disponíveis (você pode buscar do banco posteriormente)
  const centrosCustoOptions = [
    'Administrativo',
    'Produção',
    'Vendas',
    'TI',
    'RH',
    'Financeiro',
    'Marketing',
    'Logística',
    'Manutenção',
    'Qualidade'
  ];

  // Lista de responsáveis disponíveis (você pode buscar do banco posteriormente)
  const responsaveisOptions = [
    'João Silva',
    'Maria Santos',
    'Pedro Oliveira',
    'Ana Costa',
    'Carlos Souza',
    'Juliana Lima',
    'Roberto Alves',
    'Fernanda Rocha',
    'Lucas Martins',
    'Patricia Ferreira'
  ];

  // Lista de localizações disponíveis (você pode buscar do banco posteriormente)
  const localizacoesOptions = [
    'Sede - São Paulo',
    'Filial - Rio de Janeiro',
    'Filial - Belo Horizonte',
    'Depósito Central',
    'Almoxarifado',
    'Sala de TI',
    'Recepção',
    'Sala de Reuniões',
    'Escritório Administrativo',
    'Área de Produção',
    'Laboratório',
    'Garagem'
  ];

  useEffect(() => {
    if (user) {
      loadAllAssets();
    }
  }, [user]);

  const loadAllAssets = async () => {
    try {
      setLoading(true);

      console.log('🔍 Buscando todos os ativos...');

      // Buscar todos os ativos
      const { data: assets, error: assetsError } = await supabase
        .from('asset_main_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (assetsError) throw assetsError;

      console.log(`📊 Total de ativos encontrados: ${assets?.length || 0}`);

      if (!assets || assets.length === 0) {
        setMainItems([]);
        setLoading(false);
        return;
      }

      // Buscar dados das NF-es correspondentes
      const chNfes = [...new Set(assets.map(a => a.ch_nfe))];
      
      console.log(`📋 Buscando dados de ${chNfes.length} NF-es...`);

      const { data: nfes, error: nfesError } = await supabase
        .from('nfe_documents')
        .select('ch_nfe, n_nf, serie, emit_xnome, dh_emi')
        .in('ch_nfe', chNfes);

      if (nfesError) throw nfesError;

      // Criar mapa de NF-es para enriquecimento rápido
      const nfeMap = new Map(nfes?.map(nfe => [nfe.ch_nfe, nfe]) || []);

      // Enriquecer ativos com dados da NF-e
      const enrichedAssets = assets.map(asset => {
        const nfeData = nfeMap.get(asset.ch_nfe);
        return {
          ...asset,
          n_nf: nfeData?.n_nf,
          serie: nfeData?.serie,
          emit_xnome: nfeData?.emit_xnome,
          dh_emi: nfeData?.dh_emi,
          // Se data_aquisicao estiver vazia, usar a data da NF-e
          data_aquisicao: asset.data_aquisicao || nfeData?.dh_emi || null
        };
      });

      console.log('✅ Ativos enriquecidos com dados das NF-es');

      setMainItems(enrichedAssets);
    } catch (error) {
      console.error('❌ Erro ao carregar ativos:', error);
      alert('Erro ao carregar ativos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = async (item: MainItem) => {
    setSelectedItem(item);
    setLoadingSubitems(true);

    try {
      const { data: subs, error } = await supabase
        .from('asset_financial_subitems')
        .select('*')
        .eq('main_item_id', item.id)
        .order('sub_code', { ascending: true });

      if (error) throw error;

      setSubitems(subs || []);
    } catch (error) {
      console.error('Erro ao carregar sub-itens:', error);
      alert('Erro ao carregar sub-itens financeiros');
    } finally {
      setLoadingSubitems(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedItem(null);
    setSubitems([]);
  };

  const toggleExpand = (itemId: number) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const handleEditCentroCusto = (item: MainItem) => {
    setEditingCentroCusto(item.id);
    setCentroCustoValue(item.centro_custo || '');
    setShowCentroCustoDropdown(false);
  };

  const handleCancelEditCentroCusto = () => {
    setEditingCentroCusto(null);
    setCentroCustoValue('');
    setShowCentroCustoDropdown(false);
  };

  const handleSaveCentroCusto = async (itemId: number) => {
    if (savingCentroCusto) return;

    try {
      setSavingCentroCusto(true);

      console.log('💾 Salvando centro de custo:', { itemId, value: centroCustoValue });

      const { error } = await supabase
        .from('asset_main_items')
        .update({ centro_custo: centroCustoValue || null })
        .eq('id', itemId);

      if (error) throw error;

      console.log('✅ Centro de custo salvo com sucesso');

      // Atualizar a lista local
      setMainItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, centro_custo: centroCustoValue || null } : item
      ));

      // Resetar estados de edição
      setEditingCentroCusto(null);
      setCentroCustoValue('');
      setShowCentroCustoDropdown(false);

      alert('Centro de custo atualizado com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao salvar centro de custo:', error);
      alert('Erro ao salvar centro de custo. Tente novamente.');
    } finally {
      setSavingCentroCusto(false);
    }
  };

  const handleEditDataAquisicao = (item: MainItem) => {
    setEditingDataAquisicao(item.id);
    // Formatar data para input type="date" (YYYY-MM-DD)
    if (item.data_aquisicao) {
      const date = new Date(item.data_aquisicao);
      const formattedDate = date.toISOString().split('T')[0];
      setDataAquisicaoValue(formattedDate);
    } else {
      setDataAquisicaoValue('');
    }
  };

  const handleCancelEditDataAquisicao = () => {
    setEditingDataAquisicao(null);
    setDataAquisicaoValue('');
  };

  const handleSaveDataAquisicao = async (itemId: number) => {
    if (savingDataAquisicao) return;

    try {
      setSavingDataAquisicao(true);

      console.log('💾 Salvando data de aquisição:', { itemId, value: dataAquisicaoValue });

      const { error } = await supabase
        .from('asset_main_items')
        .update({ data_aquisicao: dataAquisicaoValue || null })
        .eq('id', itemId);

      if (error) throw error;

      console.log('✅ Data de aquisição salva com sucesso');

      // Atualizar a lista local
      setMainItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, data_aquisicao: dataAquisicaoValue || null } : item
      ));

      // Resetar estados de edição
      setEditingDataAquisicao(null);
      setDataAquisicaoValue('');

      alert('Data de aquisição atualizada com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao salvar data de aquisição:', error);
      alert('Erro ao salvar data de aquisição. Tente novamente.');
    } finally {
      setSavingDataAquisicao(false);
    }
  };

  const handleEditResponsavel = (item: MainItem) => {
    setEditingResponsavel(item.id);
    setResponsavelValue(item.responsavel || '');
    setShowResponsavelDropdown(false);
  };

  const handleCancelEditResponsavel = () => {
    setEditingResponsavel(null);
    setResponsavelValue('');
    setShowResponsavelDropdown(false);
  };

  const handleSaveResponsavel = async (itemId: number) => {
    if (savingResponsavel) return;

    try {
      setSavingResponsavel(true);

      console.log('💾 Salvando responsável:', { itemId, value: responsavelValue });

      const { error } = await supabase
        .from('asset_main_items')
        .update({ responsavel: responsavelValue || null })
        .eq('id', itemId);

      if (error) throw error;

      console.log('✅ Responsável salvo com sucesso');

      // Atualizar a lista local
      setMainItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, responsavel: responsavelValue || null } : item
      ));

      // Resetar estados de edição
      setEditingResponsavel(null);
      setResponsavelValue('');
      setShowResponsavelDropdown(false);

      alert('Responsável atualizado com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao salvar responsável:', error);
      alert('Erro ao salvar responsável. Tente novamente.');
    } finally {
      setSavingResponsavel(false);
    }
  };

  const handleEditLocalizacao = (item: MainItem) => {
    setEditingLocalizacao(item.id);
    setLocalizacaoValue(item.localizacao || '');
    setShowLocalizacaoDropdown(false);
  };

  const handleCancelEditLocalizacao = () => {
    setEditingLocalizacao(null);
    setLocalizacaoValue('');
    setShowLocalizacaoDropdown(false);
  };

  const handleSaveLocalizacao = async (itemId: number) => {
    if (savingLocalizacao) return;

    try {
      setSavingLocalizacao(true);

      console.log('💾 Salvando localização:', { itemId, value: localizacaoValue });

      const { error } = await supabase
        .from('asset_main_items')
        .update({ localizacao: localizacaoValue || null })
        .eq('id', itemId);

      if (error) throw error;

      console.log('✅ Localização salva com sucesso');

      // Atualizar a lista local
      setMainItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, localizacao: localizacaoValue || null } : item
      ));

      // Resetar estados de edição
      setEditingLocalizacao(null);
      setLocalizacaoValue('');
      setShowLocalizacaoDropdown(false);

      alert('Localização atualizada com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao salvar localização:', error);
      alert('Erro ao salvar localização. Tente novamente.');
    } finally {
      setSavingLocalizacao(false);
    }
  };

  const handleSelectCentroCusto = (value: string) => {
    setCentroCustoValue(value);
    setShowCentroCustoDropdown(false);
  };

  const filteredCentrosCusto = centrosCustoOptions.filter(option =>
    option.toLowerCase().includes(centroCustoValue.toLowerCase())
  );

  const handleSelectResponsavel = (value: string) => {
    setResponsavelValue(value);
    setShowResponsavelDropdown(false);
  };

  const filteredResponsaveis = responsaveisOptions.filter(option =>
    option.toLowerCase().includes(responsavelValue.toLowerCase())
  );

  const handleSelectLocalizacao = (value: string) => {
    setLocalizacaoValue(value);
    setShowLocalizacaoDropdown(false);
  };

  const filteredLocalizacoes = localizacoesOptions.filter(option =>
    option.toLowerCase().includes(localizacaoValue.toLowerCase())
  );

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'OFICIAL':
        return 'bg-green-100 text-green-800 border border-green-300';
      case 'RASCUNHO':
        return 'bg-amber-100 text-amber-800 border border-amber-300';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  const getSubitemTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      BASE: 'Base',
      FRETE: 'Frete',
      IMPOSTOS: 'Impostos',
      SEGURO: 'Seguro',
      OUTROS: 'Outros',
    };
    return labels[type] || type;
  };

  // Contar itens não inventariados
  const naoInventariadosCount = mainItems.filter(item => item.status_inventario === 'NAO_INVENTARIADO').length;

  // Função para selecionar/desselecionar item
  const toggleItemSelection = (itemId: number) => {
    const newSelected = new Set(selectedItemsForCollector);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItemsForCollector(newSelected);
  };

  // Função para selecionar todos os itens não inventariados
  const selectAllNaoInventariados = () => {
    const naoInventariados = mainItems
      .filter(item => item.status_inventario === 'NAO_INVENTARIADO')
      .map(item => item.id);
    setSelectedItemsForCollector(new Set(naoInventariados));
  };

  // Função para desselecionar todos
  const deselectAll = () => {
    setSelectedItemsForCollector(new Set());
  };

  const handleSendToCollector = async () => {
    if (sendingToCollector || selectedItemsForCollector.size === 0) return;

    // Usar itens selecionados
    const itemsToSend = mainItems.filter(item => selectedItemsForCollector.has(item.id));

    if (itemsToSend.length === 0) {
      alert('Nenhum item selecionado para enviar ao coletor.');
      return;
    }

    const confirmMessage = `Deseja enviar ${itemsToSend.length} item(ns) selecionado(s) para o coletor de dados?`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setSendingToCollector(true);

      console.log('📤 Enviando itens selecionados para o coletor de dados...');
      console.log(`Total de itens: ${itemsToSend.length}`);

      // Atualizar status dos itens para ENVIADO_COLETOR
      const itemIds = itemsToSend.map(item => item.id);

      const { error } = await supabase
        .from('asset_main_items')
        .update({ 
          status_inventario: 'ENVIADO_COLETOR',
          updated_at: new Date().toISOString()
        })
        .in('id', itemIds);

      if (error) throw error;

      console.log('✅ Itens enviados com sucesso para o coletor');

      // Atualizar a lista local
      setMainItems(prev => prev.map(item => 
        itemIds.includes(item.id) 
          ? { ...item, status_inventario: 'ENVIADO_COLETOR' } 
          : item
      ));

      // Limpar seleção
      setSelectedItemsForCollector(new Set());

      alert(`✅ ${itemsToSend.length} item(ns) enviado(s) com sucesso para o coletor de dados!`);

    } catch (error) {
      console.error('❌ Erro ao enviar itens para o coletor:', error);
      alert('Erro ao enviar itens para o coletor. Tente novamente.');
    } finally {
      setSendingToCollector(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Cabeçalho */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Cadastro de Ativos</h1>
              <p className="text-gray-600 mt-1">
                Visualize todos os ativos gerados pelo processo de importação e desmembramento
              </p>
            </div>
            
            {/* Botão de Enviar para Coletor */}
            {naoInventariadosCount > 0 && (
              <button
                onClick={handleSendToCollector}
                disabled={sendingToCollector || selectedItemsForCollector.size === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                {sendingToCollector ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    Enviar Selecionados ({selectedItemsForCollector.size})
                  </>
                )}
              </button>
            )}
          </div>

          {/* Badge de Alerta e Controles de Seleção */}
          {naoInventariadosCount > 0 && (
            <div className="mt-4 space-y-3">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <i className="ri-alert-line text-amber-600 text-xl flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">
                    Itens Pendentes de Inventário
                  </h3>
                  <p className="text-sm text-amber-800">
                    Existem <strong>{naoInventariadosCount} item(ns)</strong> com status "NÃO INVENTARIADO". 
                    Selecione os itens que deseja enviar para o coletor de dados.
                  </p>
                </div>
              </div>

              {/* Controles de Seleção */}
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-multiple-line text-blue-600 text-lg" />
                  <span className="text-sm font-medium text-blue-900">
                    {selectedItemsForCollector.size} de {naoInventariadosCount} selecionado(s)
                  </span>
                </div>
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={selectAllNaoInventariados}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    Selecionar Todos
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Desmarcar Todos
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabela de Ativos */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
          </div>
        ) : mainItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhum ativo foi gerado ainda
            </h3>
            <p className="text-gray-500 mb-6">
              Vá em Notas Fiscais e clique em "Gerar Ativos"
            </p>
            <button
              onClick={() => navigate('/invoices')}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Ir para Notas Fiscais
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Coluna de Checkbox */}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <i className="ri-checkbox-line text-lg" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número da Nota
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      N. Ativo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número da Tag
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número de Série
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Total
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Classe Contábil
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {mainItems.map((item) => {
                    const isNaoInventariado = item.status_inventario === 'NAO_INVENTARIADO';
                    const isSelected = selectedItemsForCollector.has(item.id);

                    return (
                      <>
                        <tr key={item.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                          {/* Checkbox */}
                          <td className="px-4 py-3 text-center">
                            {isNaoInventariado ? (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(item.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              />
                            ) : (
                              <span className="text-gray-300">
                                <i className="ri-checkbox-blank-line" />
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {item.n_nf && item.serie ? `${item.n_nf}/${item.serie}` : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.n_item}.{item.seq}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-teal-600 font-semibold">
                            {item.tag_patrimonial}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="max-w-xs truncate" title={item.descricao_normalizada}>
                              {item.descricao_normalizada || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-700">
                            {item.numero_serie || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                            R$ {item.valor_total.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.categoria_item || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {/* Classe Contábil - campo vazio por enquanto */}
                            -
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                                item.status_cadastro
                              )}`}
                            >
                              {item.status_cadastro}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => toggleExpand(item.id)}
                                className="text-teal-600 hover:text-teal-900 w-8 h-8 flex items-center justify-center hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                                title={expandedItem === item.id ? "Ocultar detalhes" : "Ver detalhes"}
                              >
                                <i className={`ri-arrow-${expandedItem === item.id ? 'up' : 'down'}-s-line text-lg`} />
                              </button>
                              <button
                                onClick={() => handleOpenDetails(item)}
                                className="text-blue-600 hover:text-blue-900 w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                title="Ver sub-itens financeiros"
                              >
                                <i className="ri-money-dollar-circle-line text-lg" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Linha expandida com detalhes */}
                        {expandedItem === item.id && (
                          <tr className="bg-gray-50">
                            <td colSpan={11} className="px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-building-line mr-1" />
                                    Emitente
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-sm text-gray-900">{item.emit_xnome || '-'}</p>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-calendar-line mr-1" />
                                    Data da NF-e
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-sm text-gray-900">
                                      {item.dh_emi ? new Date(item.dh_emi).toLocaleDateString('pt-BR') : '-'}
                                    </p>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-building-2-line mr-1" />
                                    Centro de Custo
                                  </label>
                                  {editingCentroCusto === item.id ? (
                                    <div className="relative">
                                      <div className="flex gap-2">
                                        <div className="flex-1 relative">
                                          <input
                                            type="text"
                                            value={centroCustoValue}
                                            onChange={(e) => {
                                              setCentroCustoValue(e.target.value);
                                              setShowCentroCustoDropdown(true);
                                            }}
                                            onFocus={() => setShowCentroCustoDropdown(true)}
                                            placeholder="Digite ou selecione..."
                                            className="w-full px-3 py-2 border border-teal-500 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                          />
                                          {showCentroCustoDropdown && filteredCentrosCusto.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                              {filteredCentrosCusto.map((option, index) => (
                                                <button
                                                  key={index}
                                                  onClick={() => handleSelectCentroCusto(option)}
                                                  className="w-full px-3 py-2 text-left text-sm hover:bg-teal-50 transition-colors cursor-pointer"
                                                >
                                                  {option}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => handleSaveCentroCusto(item.id)}
                                          disabled={savingCentroCusto}
                                          className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Salvar"
                                        >
                                          {savingCentroCusto ? (
                                            <i className="ri-loader-4-line animate-spin" />
                                          ) : (
                                            <i className="ri-check-line" />
                                          )}
                                        </button>
                                        <button
                                          onClick={handleCancelEditCentroCusto}
                                          disabled={savingCentroCusto}
                                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Cancelar"
                                        >
                                          <i className="ri-close-line" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                        <p className="text-sm text-gray-900">{item.centro_custo || '-'}</p>
                                      </div>
                                      <button
                                        onClick={() => handleEditCentroCusto(item)}
                                        className="px-3 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                                        title="Editar centro de custo"
                                      >
                                        <i className="ri-edit-line" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-map-pin-line mr-1" />
                                    Localização
                                  </label>
                                  {editingLocalizacao === item.id ? (
                                    <div className="relative">
                                      <div className="flex gap-2">
                                        <div className="flex-1 relative">
                                          <input
                                            type="text"
                                            value={localizacaoValue}
                                            onChange={(e) => {
                                              setLocalizacaoValue(e.target.value);
                                              setShowLocalizacaoDropdown(true);
                                            }}
                                            onFocus={() => setShowLocalizacaoDropdown(true)}
                                            placeholder="Digite ou selecione..."
                                            className="w-full px-3 py-2 border border-teal-500 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                          />
                                          {showLocalizacaoDropdown && filteredLocalizacoes.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                              {filteredLocalizacoes.map((option, index) => (
                                                <button
                                                  key={index}
                                                  onClick={() => handleSelectLocalizacao(option)}
                                                  className="w-full px-3 py-2 text-left text-sm hover:bg-teal-50 transition-colors cursor-pointer"
                                                >
                                                  {option}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => handleSaveLocalizacao(item.id)}
                                          disabled={savingLocalizacao}
                                          className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Salvar"
                                        >
                                          {savingLocalizacao ? (
                                            <i className="ri-loader-4-line animate-spin" />
                                          ) : (
                                            <i className="ri-check-line" />
                                          )}
                                        </button>
                                        <button
                                          onClick={handleCancelEditLocalizacao}
                                          disabled={savingLocalizacao}
                                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Cancelar"
                                        >
                                          <i className="ri-close-line" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                        <p className="text-sm text-gray-900">{item.localizacao || '-'}</p>
                                      </div>
                                      <button
                                        onClick={() => handleEditLocalizacao(item)}
                                        className="px-3 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                                        title="Editar localização"
                                      >
                                        <i className="ri-edit-line" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-user-line mr-1" />
                                    Responsável
                                  </label>
                                  {editingResponsavel === item.id ? (
                                    <div className="relative">
                                      <div className="flex gap-2">
                                        <div className="flex-1 relative">
                                          <input
                                            type="text"
                                            value={responsavelValue}
                                            onChange={(e) => {
                                              setResponsavelValue(e.target.value);
                                              setShowResponsavelDropdown(true);
                                            }}
                                            onFocus={() => setShowResponsavelDropdown(true)}
                                            placeholder="Digite ou selecione..."
                                            className="w-full px-3 py-2 border border-teal-500 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                          />
                                          {showResponsavelDropdown && filteredResponsaveis.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                              {filteredResponsaveis.map((option, index) => (
                                                <button
                                                  key={index}
                                                  onClick={() => handleSelectResponsavel(option)}
                                                  className="w-full px-3 py-2 text-left text-sm hover:bg-teal-50 transition-colors cursor-pointer"
                                                >
                                                  {option}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => handleSaveResponsavel(item.id)}
                                          disabled={savingResponsavel}
                                          className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Salvar"
                                        >
                                          {savingResponsavel ? (
                                            <i className="ri-loader-4-line animate-spin" />
                                          ) : (
                                            <i className="ri-check-line" />
                                          )}
                                        </button>
                                        <button
                                          onClick={handleCancelEditResponsavel}
                                          disabled={savingResponsavel}
                                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Cancelar"
                                        >
                                          <i className="ri-close-line" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                        <p className="text-sm text-gray-900">{item.responsavel || '-'}</p>
                                      </div>
                                      <button
                                        onClick={() => handleEditResponsavel(item)}
                                        className="px-3 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                                        title="Editar responsável"
                                      >
                                        <i className="ri-edit-line" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-calendar-check-line mr-1" />
                                    Data de Aquisição
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <div className="flex gap-2">
                                      <input
                                        type="date"
                                        value={dataAquisicaoValue}
                                        onChange={(e) => setDataAquisicaoValue(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-teal-500 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                      />
                                      <button
                                        onClick={() => handleSaveDataAquisicao(item.id)}
                                        disabled={savingDataAquisicao}
                                        className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Salvar"
                                      >
                                        {savingDataAquisicao ? (
                                          <i className="ri-loader-4-line animate-spin" />
                                        ) : (
                                          <i className="ri-check-line" />
                                        )}
                                      </button>
                                      <button
                                        onClick={handleCancelEditDataAquisicao}
                                        disabled={savingDataAquisicao}
                                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Cancelar"
                                      >
                                        <i className="ri-close-line" />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-time-line mr-1" />
                                    Vida Útil (meses)
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-sm text-gray-900">{item.vida_util_meses || '-'}</p>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-checkbox-circle-line mr-1" />
                                    Status Inventário
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-sm text-gray-900">{item.status_inventario}</p>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <i className="ri-time-line mr-1" />
                                    Criado em
                                  </label>
                                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-sm text-gray-900">
                                      {new Date(item.created_at).toLocaleString('pt-BR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>

                                {item.observacoes && (
                                  <div className="lg:col-span-3">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      <i className="ri-message-3-line mr-1" />
                                      Observações
                                    </label>
                                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                      <p className="text-sm text-gray-900">{item.observacoes}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Resumo Geral */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total de Ativos Cadastrados</p>
                  <p className="text-3xl font-bold">{mainItems.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90 mb-1">Soma Total dos Valores</p>
                  <p className="text-3xl font-bold">
                    R$ {mainItems.reduce((sum, item) => sum + item.valor_total, 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <i className="ri-bar-chart-box-line text-4xl" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Drawer de Sub-itens Financeiros */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Sub-itens Financeiros</h2>
                <p className="text-sm text-gray-600 mt-1">Tag: {selectedItem.tag_patrimonial}</p>
              </div>
              <button
                onClick={handleCloseDetails}
                className="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Informações do Ativo */}
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <h3 className="font-semibold text-gray-800 mb-3">Informações do Ativo</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Tag:</span>
                    <span className="ml-2 font-medium text-gray-900">{selectedItem.tag_patrimonial}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Item NF:</span>
                    <span className="ml-2 font-medium text-gray-900">{selectedItem.n_item}.{selectedItem.seq}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Descrição:</span>
                    <span className="ml-2 font-medium text-gray-900">{selectedItem.descricao_normalizada}</span>
                  </div>
                </div>
              </div>

              {/* Tabela de Sub-itens */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Composição do Valor
                </h3>
                {loadingSubitems ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                  </div>
                ) : subitems.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-8">Nenhum sub-item encontrado</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Código
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tipo
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Valor
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subitems.map(subitem => (
                          <tr key={subitem.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">
                              {subitem.sub_code}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {getSubitemTypeLabel(subitem.sub_type)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                              R$ {subitem.valor.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-teal-50">
                        <tr>
                          <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-900">
                            <i className="ri-calculator-line mr-2" />
                            Total do Ativo
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-teal-600 text-right">
                            R$ {subitems.reduce((sum, s) => sum + s.valor, 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* Informações Adicionais */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <i className="ri-information-line mr-2 text-blue-600" />
                  Sobre os Sub-itens
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>BASE:</strong> Valor principal do produto conforme NF-e</li>
                  <li>• <strong>FRETE:</strong> Custo de transporte rateado proporcionalmente</li>
                  <li>• <strong>IMPOSTOS:</strong> ICMS, IPI, PIS, COFINS rateados</li>
                  <li>• <strong>SEGURO:</strong> Valor do seguro rateado (se houver)</li>
                  <li>• <strong>OUTROS:</strong> Demais custos e ajustes de arredondamento</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4">
              <button
                onClick={handleCloseDetails}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
